import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Gift, Users, Copy, Check, Share2, 
  Loader2, TrendingUp, Award, Coins
} from 'lucide-react';
import { toast } from 'sonner';
import { APP_NAME } from '../lib/appConfig';
import { useLanguage } from '../i18n';

export const ReferralsPage = () => {
  const PAGE_SIZE_OPTIONS = ['5', '10', '20', '50', '100', 'all'];
  const { user } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState('10');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, listRes] = await Promise.all([
        api.getReferralStats(),
        api.getReferralsList()
      ]);
      setStats(statsRes.data);
      setReferrals(listRes.data);
    } catch (error) {
      console.error('Error loading referrals:', error);
    } finally {
      setLoading(false);
    }
  };

  const referralCode = user?.referral_code || stats?.referral_code;
  const isAllMode = pageSize === 'all';
  const pageSizeValue = isAllMode ? Math.max(referrals.length, 1) : Number(pageSize);
  const totalPages = isAllMode ? 1 : Math.max(1, Math.ceil(referrals.length / pageSizeValue));
  const safePage = isAllMode ? 1 : Math.min(currentPage, totalPages);
  const pageStart = isAllMode ? 0 : (safePage - 1) * pageSizeValue;
  const visibleReferrals = isAllMode
    ? referrals
    : referrals.slice(pageStart, pageStart + pageSizeValue);

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  useEffect(() => {
    if (safePage !== currentPage) {
      setCurrentPage(safePage);
    }
  }, [safePage, currentPage]);

  const copyReferralCode = async () => {
    if (referralCode) {
      try {
        await navigator.clipboard.writeText(referralCode);
        setCopied(true);
        toast.success(t('referralsPage.codeCopied'));
        setTimeout(() => setCopied(false), 2000);
      } catch (e) {
        console.error('Error copying referral code:', e);
      }
    }
  };

  const shareReferral = async () => {
    if (!referralCode) {
      return;
    }

    const shareUrl = `${window.location.origin}/auth?referral=${encodeURIComponent(referralCode)}`;
    const text = t('referralsPage.shareText', { appName: APP_NAME, referralCode });
    const sharePayload = `${text}\n${shareUrl}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: t('referralsPage.shareTitle', { appName: APP_NAME }),
          text,
          url: shareUrl
        });
      } catch (e) {
        // User cancelled
      }
    } else {
      try {
        await navigator.clipboard.writeText(sharePayload);
        toast.success(t('referralsPage.linkCopied'));
      } catch (e) {
        console.error('Error copying referral share payload:', e);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  const bonuses = [
    {
      icon: Coins,
      title: 'Bonus cash',
      value: '500 XOF',
      description: 'Pour chaque filleul qui cotise',
      color: 'bg-green-100 text-green-600'
    },
    {
      icon: Award,
      title: 'Points fidélité',
      value: '100 points',
      description: 'Cumulables pour des avantages',
      color: 'bg-purple-100 text-purple-600'
    },
    {
      icon: TrendingUp,
      title: 'Réduction frais',
      value: '10%',
      description: 'Sur les frais de plateforme',
      color: 'bg-blue-100 text-blue-600'
    }
  ];

  return (
    <div className="space-y-6" data-testid="referrals-page">
      {/* Header */}
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-stone-900 dark:text-stone-50">
          {t('referralsPage.title')}
        </h1>
        <p className="text-stone-600 dark:text-stone-400 mt-1">
          {t('referralsPage.subtitle')}
        </p>
      </div>

      {/* Referral Code Card */}
      <Card className="card-neo bg-gradient-to-br from-orange-50 to-orange-100/50 dark:from-orange-900/20 dark:to-orange-900/10 border-orange-200 dark:border-orange-800">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <p className="text-sm text-orange-700 dark:text-orange-400 mb-1">{t('referralsPage.yourCode')}</p>
              <div className="flex items-center gap-3">
                <span className="text-2xl sm:text-3xl font-bold text-orange-900 dark:text-orange-100 font-mono tracking-wider break-all">
                  {referralCode || '...'}
                </span>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={copyReferralCode}
                  className="border-orange-300"
                  data-testid="copy-referral-code"
                >
                  {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <Button 
              className="btn-accent gap-2 w-full md:w-auto"
              onClick={shareReferral}
              disabled={!referralCode}
              data-testid="share-referral-btn"
            >
              <Share2 className="w-4 h-4" />
              {t('referralsPage.shareAction')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="stat-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wider">{t('referralsPage.stats.referrals')}</p>
                <p className="text-2xl font-bold text-stone-900 dark:text-stone-50 tabular-nums">
                  {stats?.total_referrals || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wider">{t('referralsPage.stats.active')}</p>
                <p className="text-2xl font-bold text-stone-900 dark:text-stone-50 tabular-nums">
                  {stats?.successful_referrals || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center">
                <Gift className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wider">{t('referralsPage.stats.bonusEarned')}</p>
                <p className="text-xl font-bold text-stone-900 dark:text-stone-50 tabular-nums">
                  {(stats?.total_bonus_earned || 0).toLocaleString()} XOF
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
                <Award className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wider">{t('referralsPage.stats.points')}</p>
                <p className="text-2xl font-bold text-stone-900 dark:text-stone-50 tabular-nums">
                  {user?.points || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bonus Types */}
      <div>
        <h2 className="font-display text-xl font-bold text-stone-900 dark:text-stone-50 mb-4">
          {t('referralsPage.benefitsTitle')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {bonuses.map((bonus, index) => (
            <Card key={index} className="card-neo">
              <CardContent className="p-5">
                <div className={`w-12 h-12 ${bonus.color} rounded-xl flex items-center justify-center mb-4`}>
                  <bonus.icon className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-stone-900 dark:text-stone-50 mb-1">
                  {bonus.title}
                </h3>
                <p className="text-2xl font-bold text-stone-900 dark:text-stone-50 mb-2">
                  {bonus.value}
                </p>
                <p className="text-sm text-stone-500">{bonus.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Referrals List */}
      <Card className="card-neo">
        <CardHeader>
          <CardTitle className="font-display text-lg">
            {t('referralsPage.myReferrals', { count: referrals.length })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {referrals.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <p className="text-stone-500 mb-4">{t('referralsPage.noReferrals')}</p>
              <p className="text-sm text-stone-400 max-w-md mx-auto">
                {t('referralsPage.noReferralsHelp')}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {visibleReferrals.map((referral, index) => (
                <div 
                  key={`${referral.referee_email}-${index}`}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-xl bg-stone-50 dark:bg-stone-800/50"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-stone-200 dark:bg-stone-700 rounded-full flex items-center justify-center">
                      <span className="font-medium text-stone-600 dark:text-stone-300">
                        {referral.referee_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-stone-900 dark:text-stone-50">
                        {referral.referee_name}
                      </p>
                      <p className="text-sm text-stone-500">
                        {referral.referee_email}
                      </p>
                    </div>
                  </div>
                  <Badge 
                    variant={referral.status === 'completed' ? 'default' : 'secondary'}
                    className={referral.status === 'completed' ? 'bg-green-100 text-green-700 self-start sm:self-auto' : 'self-start sm:self-auto'}
                  >
                    {referral.status === 'completed' ? t('referralsPage.status.active') : t('referralsPage.status.pending')}
                  </Badge>
                </div>
              ))}

              <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-testid="referrals-pagination">
                <p className="text-xs text-stone-500" data-testid="referrals-page-indicator">
                  {t('common.page')} {safePage} / {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <Select value={pageSize} onValueChange={setPageSize}>
                    <SelectTrigger className="w-[120px] h-8" data-testid="referrals-page-size-select">
                      <SelectValue placeholder={t('common.pageSize')} />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option === 'all' ? t('common.all') : option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={safePage === 1 || isAllMode}
                    data-testid="referrals-prev-btn"
                  >
                    {t('common.previous')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={safePage >= totalPages || isAllMode}
                    data-testid="referrals-next-btn"
                  >
                    {t('common.next')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReferralsPage;
